import React from 'react'
import { Container, Grid, Stack, Skeleton, Group, Box, Card } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'

export const GameDetailPageSkeleton: React.FC = () => {
    const isLargeScreen = useMediaQuery('(min-width: 768px)')

    return (
        <Container size="xl" py="xl">
            <Grid gutter="lg">
                {/* Game Image Column */}
                <Grid.Col span={{ base: 12, sm: isLargeScreen ? 3 : 12 }}>
                    <Box style={{
                        maxWidth: isLargeScreen ? '280px' : '200px',
                        margin: isLargeScreen ? '0 auto 0 0' : '0 auto',
                        position: 'relative'
                    }}>
                        <Skeleton height={383} radius="md" />
                    </Box>
                </Grid.Col>

                {/* Game Details Column */}
                <Grid.Col span={{ base: 12, sm: isLargeScreen ? 9 : 12 }}>
                    <Stack gap={isLargeScreen ? "sm" : "md"}>
                        {/* Title and Platform */}
                        <div>
                            <Group justify="space-between" align="flex-start">
                                <div style={{ flex: 1 }}>
                                    <Skeleton height={32} width="80%" radius="sm" mb="xs" />
                                    <Group gap="sm" align="center" wrap="wrap">
                                        <Skeleton height={24} width={100} radius="sm" />
                                        <Skeleton height={16} width={120} radius="sm" />
                                    </Group>
                                </div>
                                <Skeleton height={36} width={120} radius="sm" />
                            </Group>
                        </div>

                        {/* Description */}
                        <div>
                            <Skeleton height={16} width="100%" radius="sm" mb="xs" />
                            <Skeleton height={16} width="95%" radius="sm" mb="xs" />
                            <Skeleton height={16} width="90%" radius="sm" mb="xs" />
                            <Skeleton height={20} width={100} radius="sm" />
                        </div>

                        {/* Game Info */}
                        <Group gap="lg">
                            <div>
                                <Skeleton height={14} width={80} radius="sm" mb="xs" />
                                <Skeleton height={16} width={100} radius="sm" />
                            </div>
                            <div>
                                <Skeleton height={14} width={60} radius="sm" mb="xs" />
                                <Skeleton height={24} width={80} radius="sm" />
                            </div>
                            <div>
                                <Skeleton height={14} width={70} radius="sm" mb="xs" />
                                <Group gap="xs">
                                    <Skeleton height={20} width={60} radius="sm" />
                                    <Skeleton height={24} width={60} radius="sm" />
                                </Group>
                            </div>
                        </Group>

                        {/* Platform Selection Buttons */}
                        {isLargeScreen && (
                            <div style={{ marginBottom: '8px' }}>
                                <Group gap="sm" wrap="wrap">
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <Skeleton key={index} height={40} width={120} radius="sm" />
                                    ))}
                                </Group>
                            </div>
                        )}

                        {/* Best Price */}
                        <div>
                            <Skeleton height={14} width={80} radius="sm" mb="xs" />
                            <Group align="center" gap="sm">
                                <Skeleton height={32} width={100} radius="sm" />
                                <Skeleton height={36} width={150} radius="sm" />
                            </Group>
                        </div>

                        {/* Wishlist Actions */}
                        <Group gap="sm">
                            <Skeleton height={36} width={140} radius="sm" />
                            <Skeleton height={36} width={160} radius="sm" />
                        </Group>

                        {/* Genres */}
                        <div>
                            <Skeleton height={14} width={80} radius="sm" mb="xs" />
                            <Group gap="xs">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <Skeleton key={index} height={24} width={80} radius="sm" />
                                ))}
                            </Group>
                        </div>
                    </Stack>
                </Grid.Col>
            </Grid>

            {/* Divider */}
            <Skeleton height={1} width="100%" radius="sm" my="xl" />

            {/* Platform Selection - Small Screens */}
            {!isLargeScreen && (
                <Box mb="xl">
                    <Skeleton height={28} width={200} radius="sm" mb="md" />
                    <Skeleton height={48} width="100%" radius="sm" />
                </Box>
            )}

            {/* Prices Section */}
            <Box mb="xl">
                <Skeleton height={28} width={150} radius="sm" mb="md" />
                <Grid>
                    <Grid.Col span={{ base: 12, sm: isLargeScreen ? 6 : 12 }}>
                        <Stack gap="sm">
                            {Array.from({ length: 3 }).map((_, index) => (
                                <Card key={index} shadow="sm" padding={isLargeScreen ? "lg" : "md"} radius="md" withBorder>
                                    {isLargeScreen ? (
                                        <Group justify="space-between" align="center">
                                            <Group align="center" gap="md">
                                                <div>
                                                    <Group gap="xs" align="center">
                                                        <Skeleton height={18} width={120} radius="sm" />
                                                        <Skeleton height={16} width={60} radius="sm" />
                                                    </Group>
                                                </div>
                                            </Group>
                                            <Group align="center" gap="sm">
                                                <Skeleton height={28} width={80} radius="sm" />
                                                <Skeleton height={32} width={100} radius="sm" />
                                            </Group>
                                        </Group>
                                    ) : (
                                        <Stack gap="sm">
                                            <Group justify="space-between" align="center">
                                                <div>
                                                    <Group gap="xs" align="center">
                                                        <Skeleton height={16} width={100} radius="sm" />
                                                        <Skeleton height={16} width={50} radius="sm" />
                                                    </Group>
                                                </div>
                                                <Skeleton height={28} width={80} radius="sm" />
                                            </Group>
                                            <Group gap="xs" justify="space-between">
                                                <Skeleton height={32} width="48%" radius="sm" />
                                                <Skeleton height={32} width="48%" radius="sm" />
                                            </Group>
                                        </Stack>
                                    )}
                                </Card>
                            ))}
                        </Stack>
                    </Grid.Col>

                    {/* Price Graph for Large Screens */}
                    {isLargeScreen && (
                        <Grid.Col span={6}>
                            <Skeleton height={500} width="100%" radius="md" />
                        </Grid.Col>
                    )}
                </Grid>
            </Box>

            {/* Comments Section */}
            <Box mb="xl">
                <Skeleton height={28} width={150} radius="sm" mb="md" />
                <Skeleton height={200} width="100%" radius="md" />
            </Box>

            {/* Related Games Section */}
            <Box mb="xl">
                <Skeleton height={28} width={200} radius="sm" mb="md" />
                <Skeleton height={340} width="100%" radius="md" />
            </Box>

            {/* Product IDs Section */}
            <Box mb="xl">
                <Skeleton height={28} width={200} radius="sm" mb="md" />
                <Group gap="sm">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton key={index} height={32} width={120} radius="sm" />
                    ))}
                </Group>
            </Box>
        </Container>
    )
} 